import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

// CDR Report (2026-10-27) — Billing › Billing Reports › "CDR Report".
// Call details (like the old "Call Details Record List") + usage by extension, filtered by
// period, direction (outbound / inbound / internal), customer, centre, location, extension,
// result and free-text search; Excel / CSV export of exactly what is filtered. Read-only.

const card: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 16, color: '#1e293b' };
const sel: React.CSSProperties = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff', color: '#1e293b' };
const btn = (primary?: boolean): React.CSSProperties => ({ padding: '7px 14px', borderRadius: 6, border: primary ? 'none' : '1px solid #cbd5e1', background: primary ? '#2563eb' : '#fff', color: primary ? '#fff' : '#1e293b', cursor: 'pointer', fontWeight: 600, fontSize: 13 });
const th: React.CSSProperties = { borderBottom: '2px solid #cbd5e1', padding: '6px 8px', fontSize: 12, textAlign: 'left', background: '#f1f5f9', fontWeight: 600, whiteSpace: 'nowrap', position: 'sticky', top: 0 };
const td: React.CSSProperties = { borderBottom: '1px solid #eef2f7', padding: '5px 8px', fontSize: 12.5, whiteSpace: 'nowrap' };
const lab: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, color: '#475569', fontWeight: 600 };

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const hms = (s: number) => { s = Math.round(s || 0); return `${Math.floor(s / 3600)}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`; };
function range(p: string): [string, string] {
  const n = new Date();
  if (p === 'today') return [ymd(n), ymd(n)];
  if (p === 'yesterday') { const y = new Date(n); y.setDate(n.getDate() - 1); return [ymd(y), ymd(y)]; }
  if (p === 'last7') { const a = new Date(n); a.setDate(n.getDate() - 6); return [ymd(a), ymd(n)]; }
  if (p === 'last_month') return [ymd(new Date(n.getFullYear(), n.getMonth() - 1, 1)), ymd(new Date(n.getFullYear(), n.getMonth(), 0))];
  return [ymd(new Date(n.getFullYear(), n.getMonth(), 1)), ymd(n)];
}
const DIR: Record<string, string> = { outbound: 'Outbound', inbound: 'Inbound', internal: 'Internal' };

export default function CdrReport({ siteId: siteCtx = '' }: any) {
  const [opts, setOpts] = useState<any>({ customers: [], sites: [], locations: [], customers_enabled: false });
  const [view, setView] = useState<'calls' | 'usage'>('calls');
  const [preset, setPreset] = useState('this_month');
  const [[from, to], setRange] = useState<[string, string]>(range('this_month'));
  const [direction, setDirection] = useState('outbound');
  const [customerId, setCustomerId] = useState('');
  const [siteId, setSiteId] = useState(siteCtx); // starts on the "Managing site" selection
  const [locId, setLocId] = useState('');
  useEffect(() => { setSiteId(siteCtx); setLocId(''); }, [siteCtx]);
  const [extension, setExtension] = useState('');
  const [disposition, setDisposition] = useState('');
  const [q, setQ] = useState('');
  const [currency, setCurrency] = useState('AED');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { axios.get('/api/billing/cdr-report/options').then((r) => setOpts(r.data)).catch(() => {}); }, []);
  const locs = useMemo(() => (opts.locations || []).filter((l: any) => !siteId || String(l.site_id) === siteId), [opts, siteId]);
  const params = (extra: any = {}) => ({ from, to, direction, customer_id: customerId || undefined, site_id: siteId || undefined, location_id: locId || undefined,
    extension: extension.trim() || undefined, disposition: disposition || undefined, q: q.trim() || undefined, currency, ...extra });

  const run = async (p = 1, v = view) => {
    setBusy(true); setMsg('');
    try {
      const r = v === 'usage' ? await axios.get('/api/billing/cdr-report/usage', { params: params() })
                              : await axios.get('/api/billing/cdr-report', { params: params({ page: p, size: 100 }) });
      setData({ view: v, ...r.data }); setPage(p);
    } catch (e: any) { setMsg(e?.response?.data?.error || 'Could not build the report'); }
    finally { setBusy(false); }
  };
  const exportFile = async (kind: 'xlsx' | 'csv') => {
    setMsg('');
    try {
      const r = await axios.get(`/api/billing/cdr-report/${kind}`, { params: params({ view }), responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = `${view === 'usage' ? 'Extension-Usage' : 'CDR-Report'}_${from}_to_${to}.${kind}`; a.click(); URL.revokeObjectURL(url);
    } catch { setMsg('Could not create the file'); }
  };
  const pages = data && data.view === 'calls' ? Math.max(1, Math.ceil((data.total || 0) / (data.size || 100))) : 1;
  const money = (v: any) => (v == null ? '' : Number(v).toFixed(currency === 'AED' ? 2 : 4));

  return (
    <div style={{ color: '#1e293b' }}>
      <div style={card}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {(['calls', 'usage'] as const).map((v) => (
            <button key={v} style={btn(view === v)} onClick={() => { setView(v); setData(null); }}>{v === 'calls' ? 'Call details (CDR)' : 'Usage by extension'}</button>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label style={lab}>Period
            <select style={sel} value={preset} onChange={(e) => { setPreset(e.target.value); if (e.target.value !== 'custom') setRange(range(e.target.value)); }}>
              <option value="today">Today</option><option value="yesterday">Yesterday</option><option value="last7">Last 7 days</option>
              <option value="this_month">This month</option><option value="last_month">Last month</option><option value="custom">Custom…</option>
            </select></label>
          <label style={lab}>From<input type="date" style={sel} value={from} onChange={(e) => { setPreset('custom'); setRange([e.target.value, to]); }} /></label>
          <label style={lab}>To<input type="date" style={sel} value={to} onChange={(e) => { setPreset('custom'); setRange([from, e.target.value]); }} /></label>
          <label style={lab}>Direction
            <select style={sel} value={direction} onChange={(e) => setDirection(e.target.value)}>
              <option value="all">All calls</option><option value="outbound">Outbound</option><option value="inbound">Inbound</option>{view === 'calls' && <option value="internal">Internal</option>}
            </select></label>
          {opts.customers_enabled && <label style={lab}>Customer
            <select style={{ ...sel, maxWidth: 220 }} value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">All customers</option>{(opts.customers || []).map((c: any) => <option key={c.id} value={c.id}>{c.name}{c.code ? ` (${c.code})` : ''}</option>)}
            </select></label>}
          {(opts.sites || []).length > 0 && <label style={lab}>Centre
            <select style={sel} value={siteId} onChange={(e) => { setSiteId(e.target.value); setLocId(''); }}>
              <option value="">All centres</option>{opts.sites.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select></label>}
          {locs.length > 0 && <label style={lab}>Location
            <select style={sel} value={locId} onChange={(e) => setLocId(e.target.value)}>
              <option value="">All locations</option>{locs.map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select></label>}
          <label style={lab}>Extension<input style={{ ...sel, width: 90 }} value={extension} placeholder="e.g. 0504" onChange={(e) => setExtension(e.target.value)} /></label>
          {view === 'calls' && <label style={lab}>Result
            <select style={sel} value={disposition} onChange={(e) => setDisposition(e.target.value)}>
              <option value="">Any</option><option value="ANSWERED">Answered</option><option value="NO ANSWER">No answer</option><option value="BUSY">Busy</option><option value="FAILED">Failed</option>
            </select></label>}
          {view === 'calls' && <label style={lab}>Search<input style={{ ...sel, width: 150 }} value={q} placeholder="number or name" onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') run(1); }} /></label>}
          <label style={lab}>Currency<select style={sel} value={currency} onChange={(e) => setCurrency(e.target.value)}><option>AED</option><option>USD</option></select></label>
          <button style={btn(true)} disabled={busy} onClick={() => run(1)}>{busy ? 'Running…' : 'Run report'}</button>
          <button style={btn()} disabled={!data} onClick={() => exportFile('xlsx')}>Export Excel</button>
          <button style={btn()} disabled={!data} onClick={() => exportFile('csv')}>Export CSV</button>
        </div>
        {msg && <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 13 }}>{msg}</div>}
      </div>

      {data && data.view === 'calls' && (
        <div style={card}>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13, marginBottom: 10 }}>
            <span><b>{data.totals.calls}</b> calls</span><span><b>{data.totals.answered}</b> answered</span>
            <span>Billed time <b>{hms(data.totals.billsec)}</b></span><span>Cost <b>{money(data.totals.cost)} {data.currency}</b></span>
            <span style={{ color: '#64748b' }}>Times shown in Dubai time</span>
          </div>
          <div style={{ overflow: 'auto', maxHeight: 560 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>{['Time', 'Customer', 'Caller', 'Caller ID', 'Extension', 'Destination', 'Direction', 'Duration', 'Bill sec', 'Result', `Cost (${data.currency})`].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {data.rows.map((r: any, i: number) => (
                  <tr key={i}>
                    <td style={td}>{r.time}</td><td style={td}>{r.customer}</td><td style={td}>{r.caller_name}</td><td style={td}>{r.caller_id}</td>
                    <td style={td}>{r.extension}</td><td style={td}>{r.destination}</td><td style={td}>{DIR[r.direction] || r.direction}</td>
                    <td style={td}>{r.duration}</td><td style={td}>{r.billsec}</td><td style={td}>{r.disposition}</td><td style={{ ...td, textAlign: 'right' }}>{money(r.cost)}</td>
                  </tr>
                ))}
                {!data.rows.length && <tr><td style={td} colSpan={11}>No calls match these filters.</td></tr>}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, fontSize: 13 }}>
            <button style={btn()} disabled={busy || page <= 1} onClick={() => run(page - 1)}>‹ Previous</button>
            <span>Page {page} of {pages} · showing {data.rows.length} of {data.total}</span>
            <button style={btn()} disabled={busy || page >= pages} onClick={() => run(page + 1)}>Next ›</button>
          </div>
        </div>
      )}

      {data && data.view === 'usage' && (
        <div style={card}>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13, marginBottom: 10 }}>
            <span>Outbound <b>{data.totals.out_calls}</b> calls · <b>{hms(data.totals.out_billsec)}</b> · <b>{money(data.totals.cost)} {data.currency}</b></span>
            <span>Inbound <b>{data.totals.in_calls}</b> calls · <b>{hms(data.totals.in_billsec)}</b></span>
          </div>
          <div style={{ overflow: 'auto', maxHeight: 560 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>{['Customer', 'Location', 'Extension', 'User', 'Out calls', 'Out answered', 'Out minutes', `Cost (${data.currency})`, 'In calls', 'In answered', 'In minutes'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {data.rows.map((r: any, i: number) => (
                  <tr key={i}>
                    <td style={td}>{r.customer}</td><td style={td}>{r.location}</td><td style={td}>{r.extension}</td><td style={td}>{r.user}</td>
                    <td style={td}>{r.out_calls}</td><td style={td}>{r.out_answered}</td><td style={td}>{r.out_minutes}</td><td style={{ ...td, textAlign: 'right' }}>{money(r.cost)}</td>
                    <td style={td}>{r.in_calls}</td><td style={td}>{r.in_answered}</td><td style={td}>{r.in_minutes}</td>
                  </tr>
                ))}
                {!data.rows.length && <tr><td style={td} colSpan={11}>No usage for these filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
